import {
  CountryAlpha2,
  CountryAlpha3,
  getCountryAlpha2,
  isValidPhoneNumber,
  isValidPostalCode,
} from '@nemuru-eng/js-api-client';
import { nativeEnum, z } from 'zod';

import { GetTextIdTranslationType } from '@/components/shared/hooks/useTranslation';

// error messages shortcuts

export const requiredError = (translate: GetTextIdTranslationType) => ({
  required_error: translate('REQUIRED_FIELD'),
});

export const requiredMessage = (translate: GetTextIdTranslationType) => ({
  message: translate('REQUIRED_FIELD'),
});

export const minStringMessage = (translate: GetTextIdTranslationType) => ({
  message: translate('REQUIRED_MIN_LENGTH'),
});

export const maxStringMessage = (translate: GetTextIdTranslationType) => ({
  message: translate('REQUIRED_MAX_LENGTH'),
});

export const minNumberMessage = (translate: GetTextIdTranslationType, min?: number) => ({
  message: min ? `${translate('MIN_NUMBER_ERROR')} (${min})` : translate('MIN_NUMBER_ERROR'),
});

export const maxNumberMessage = (translate: GetTextIdTranslationType, max?: number) => ({
  message: max ? `${translate('MAX_NUMBER_EXCEEDED')} (${max})` : translate('MAX_NUMBER_EXCEEDED'),
});

export const invalidEmail = (translate: GetTextIdTranslationType) => ({
  message: translate('INVALID_EMAIL'),
});

export const invalidUrl = (translate: GetTextIdTranslationType) => ({
  message: translate('INVALID_URL'),
});

export const invalidDate = (translate: GetTextIdTranslationType) => ({
  message: translate('INVALID_DATE'),
});

export const invalidNumberError = (translate: GetTextIdTranslationType) => ({
  invalid_type_error: translate('VALUE_MUST_BE_A_NUMBER'),
});

export const discriminatedUnionError = (translate: GetTextIdTranslationType) => ({
  errorMap: (issue, ctx) => {
    if (issue.code === 'invalid_union_discriminator') return { message: translate('REQUIRED_SELECT_FIELD') };
    return { message: ctx.defaultError };
  },
});

// repeated validation schemas

export const requiredStringToNumber = (
  translate: GetTextIdTranslationType,
  minValue: number = 1,
  maxValue: number = 999999999999,
) =>
  z
    .string(requiredError(translate))
    .min(minValue, requiredMessage(translate))
    .max(maxValue, maxNumberMessage(translate))
    .pipe(z.coerce.number(invalidNumberError(translate)));

export const numberToString = (val: number) => z.coerce.string().parse(val);

// complex validations

export const validateDate = ({
  date,
  disablePast,
  disableFuture,
  checkinDate,
  departureDate,
  startDate,
}: {
  date: Date | string;
  disablePast?: boolean;
  disableFuture?: boolean;
  checkinDate?: Date;
  departureDate?: Date;
  startDate?: Date;
}): string | undefined => {
  const time = date instanceof Date ? date : new Date(date);
  const today = new Date().setHours(0, 0, 0, 0);

  if (isNaN(time.getTime())) {
    return 'INVALID_DATE';
  }
  if (today > time.getTime() && disablePast) {
    return 'PAST_DATE_NOT_ALLOWED';
  }
  if (today < time.getTime() && disableFuture) {
    return 'FUTURE_DATE_NOT_ALLOWED';
  }
  if (checkinDate && time <= checkinDate) {
    return 'CHECKOUT_DATE_CANNOT_BE_PRIOR_TO_CHECKIN_DATE';
  }
  if (departureDate && time <= departureDate) {
    return 'RETURN_DATE_CANNOT_BE_PRIOR_TO_DEPARTURE_DATE';
  }
  if (startDate && time <= startDate) {
    return 'END_DATE_CANNOT_BE_PRIOR_TO_START_DATE';
  }
};

export const requiredString = (translate: GetTextIdTranslationType) =>
  z.string(requiredError(translate)).trim().min(1, requiredMessage(translate));

export const requiredEmail = (translate: GetTextIdTranslationType) =>
  z.string(requiredError(translate)).email(invalidEmail(translate));

export const optionalEmail = (translate: GetTextIdTranslationType) =>
  z.union([z.literal(''), z.string().email(invalidEmail(translate))]).optional();

export const requiredUrl = (translate: GetTextIdTranslationType) =>
  z.string(requiredError(translate)).url(invalidUrl(translate));

export const optionalUrl = (translate: GetTextIdTranslationType) => z.string().url(invalidUrl(translate)).optional();

export const requiredNumber = (
  translate: GetTextIdTranslationType,
  minNumber: number = 1,
  maxNumber: number = 99999999999,
) =>
  z
    .number(requiredError(translate))
    .min(minNumber, minNumberMessage(translate, minNumber))
    .max(maxNumber, maxNumberMessage(translate, maxNumber));

export const optionalStringToNumber = (
  translate: GetTextIdTranslationType,
  minNumber: number = 1,
  maxNumber: number = 99999999999,
) =>
  z
    .union([
      z.literal(''),
      z
        .string()
        .transform((value) => parseInt(value))
        .pipe(
          z.coerce
            .number()
            .min(minNumber, minNumberMessage(translate, minNumber))
            .max(maxNumber, maxNumberMessage(translate, maxNumber)),
        ),
    ])
    .optional();

export const optionalStringToNumberMap = (value: string | number | null | undefined) =>
  value === '' || value === undefined ? null : Number(value) ?? null;

export const requiredEnumArray = (enumValues, translate: GetTextIdTranslationType, isNullable?: boolean) =>
  isNullable ? z.array(nativeEnum(enumValues)) : z.array(nativeEnum(enumValues)).nonempty(translate('REQUIRED_FIELD'));

export const requiredEnum = <T extends string>(selectEnum: Record<string, T>, translate?: GetTextIdTranslationType) => {
  return z.nativeEnum(selectEnum, translate && { message: translate('REQUIRED_FIELD') });
};

export const requiredDate = (translate: GetTextIdTranslationType) => z.date(requiredMessage(translate));

const dateValidation = ({
  translate,
  disablePast = false,
  disableFuture = false,
}: {
  translate: GetTextIdTranslationType;
  disablePast?: boolean;
  disableFuture?: boolean;
}) =>
  requiredDate(translate).refine(
    (val) => !validateDate({ date: val, disablePast, disableFuture }),
    (val) => {
      const invalidDateMessage = validateDate({ date: val, disablePast, disableFuture });
      if (invalidDateMessage) return { message: translate(invalidDateMessage) };
      return { message: '' };
    },
  );

export const requiredFutureDate = (translate: GetTextIdTranslationType) =>
  dateValidation({ translate, disablePast: true });

export const requiredPastDate = (translate: GetTextIdTranslationType) =>
  dateValidation({ translate, disableFuture: true });

export const requiredPhoneNumber = (translate: GetTextIdTranslationType) => {
  return z
    .string(requiredError(translate))
    .trim()
    .min(1, requiredMessage(translate))
    .transform((v) => v.replace(/\D/g, ''));
};

export const optionalPhoneNumber = z
  .string()
  .trim()
  .transform((v) => v.replace(/\D/g, ''))
  .optional();

export const optionalDialCode = z
  .object({ dialCode: z.string(), name: z.string(), iso2: z.string() })
  .nullable()
  .optional();

export const requiredDialCode = (translate: GetTextIdTranslationType) => {
  return z.object({ dialCode: z.string(), name: z.string(), iso2: z.string() }, requiredMessage(translate));
};

export const phoneValidation = (data) =>
  data.phoneNumber || data.dialCode.dialCode
    ? isValidPhoneNumber(`+${data.dialCode.dialCode}${data.phoneNumber}`)
    : true;

export const postalCodeValidation = (country: CountryAlpha2 | CountryAlpha3, postalCode: string) => {
  if (!country || !postalCode) return true;
  const countryAlpha2 = (country.length === 2 ? country : getCountryAlpha2(country as CountryAlpha3)) as CountryAlpha2;

  try {
    return isValidPostalCode(postalCode, countryAlpha2);
  } catch (error: unknown) {
    if ((error as Error).message.includes('not supported for postal code validation')) {
      return true;
    }
    throw new Error();
  }
};

export const translateZodNativeErrors = (error: string | undefined | null, t: GetTextIdTranslationType) => {
  if (!error) return null;
  switch (error) {
    case 'Expected string, received null':
      return t('REQUIRED_FIELD');
    default:
      return error;
  }
};

export const jsonValidation = (value: string): boolean => {
  try {
    JSON.parse(value);
    return true;
  } catch (e) {
    return false;
  }
};
